import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Typography,
  Table,
  Button,
  Space,
  Tag,
  Select,
  App,
  Drawer,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Modal,
  Tabs,
  Alert,
  Tooltip,
  AutoComplete,
  Segmented,
} from 'antd';
import {
  PlusOutlined,
  SendOutlined,
  StopOutlined,
  TeamOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import {
  LegalDocumentStatus,
  LegalDocumentSummary,
  LegalDocument,
  CreateLegalDocumentRequest,
  UpdateLegalDocumentRequest,
  DocumentAcceptanceUserEntry,
} from '@mygate/common';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { legalDocumentService } from '../services/legalDocuments';

const { Text } = Typography;

const STATUS_COLORS: Record<LegalDocumentStatus, string> = {
  [LegalDocumentStatus.DRAFT]: 'default',
  [LegalDocumentStatus.ACTIVE]: 'green',
  [LegalDocumentStatus.ARCHIVED]: 'orange',
};

const PREDEFINED_SLUGS = [
  {
    value: 'terms-and-conditions',
    label: 'terms-and-conditions',
    description: 'AGB / Terms & Conditions — shown on registration and in app settings',
  },
  {
    value: 'privacy-policy',
    label: 'privacy-policy',
    description: 'Datenschutzerklärung / Privacy Policy — shown on registration and in app settings',
  },
];

function MarkdownEditorWithPreview({ value, onChange }: { value?: string; onChange?: (val: string) => void }) {
  const [activeTab, setActiveTab] = useState('edit');
  const safeHtml = useMemo(
    () => (activeTab === 'preview' && value ? DOMPurify.sanitize(marked.parse(value, { async: false })) : ''),
    [activeTab, value],
  );

  return (
    <Tabs
      activeKey={activeTab}
      onChange={setActiveTab}
      items={[
        {
          key: 'edit',
          label: 'Edit',
          children: (
            <Input.TextArea
              rows={20}
              value={value}
              onChange={e => onChange?.(e.target.value)}
              placeholder="# Terms & Conditions\n\nEnter markdown content..."
              style={{ fontFamily: 'monospace' }}
            />
          ),
        },
        {
          key: 'preview',
          label: 'Preview',
          children: (
            <div
              style={{
                minHeight: 400,
                padding: 16,
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                overflow: 'auto',
                maxHeight: 600,
              }}
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          ),
        },
      ]}
    />
  );
}

export function LegalDocumentsPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [slugFilter, setSlugFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<LegalDocumentStatus | undefined>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<LegalDocument | null>(null);
  const [contentType, setContentType] = useState<'markdown' | 'url'>('markdown');
  const [form] = Form.useForm();

  // Acceptance users modal state
  const [acceptanceModalOpen, setAcceptanceModalOpen] = useState(false);
  const [acceptanceDocId, setAcceptanceDocId] = useState<string | null>(null);
  const [acceptanceFilter, setAcceptanceFilter] = useState<'accepted' | 'pending'>('accepted');
  const [acceptancePage, setAcceptancePage] = useState(1);

  // Queries
  const { data: listData, isLoading } = useQuery({
    queryKey: ['legal-documents', slugFilter, statusFilter],
    queryFn: async () => {
      const res = await legalDocumentService.list({ slug: slugFilter, status: statusFilter });
      return res.data;
    },
  });

  const { data: slugsData } = useQuery({
    queryKey: ['legal-documents', 'slugs'],
    queryFn: async () => {
      const res = await legalDocumentService.getSlugs();
      return res.data?.slugs || [];
    },
  });

  const initializedRef = useRef(false);

  const { data: acceptanceData, isLoading: acceptanceLoading } = useQuery({
    queryKey: ['legal-documents', 'acceptances', acceptanceDocId, acceptanceFilter, acceptancePage],
    queryFn: async () => {
      if (!acceptanceDocId) return null;
      const res = await legalDocumentService.getAcceptanceUsers(acceptanceDocId, acceptanceFilter, acceptancePage, 50);
      return res.data;
    },
    enabled: !!acceptanceDocId && acceptanceModalOpen,
  });

  useEffect(() => {
    if (initializedRef.current) {
      setAcceptancePage(1);
    }
  }, [acceptanceFilter]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateLegalDocumentRequest) => legalDocumentService.create(data),
    onSuccess: () => {
      message.success('Document created');
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
      setDrawerOpen(false);
      form.resetFields();
    },
    onError: () => message.error('Failed to create document'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLegalDocumentRequest }) =>
      legalDocumentService.update(id, data),
    onSuccess: () => {
      message.success('Document updated');
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
      setDrawerOpen(false);
      setEditingDoc(null);
    },
    onError: () => message.error('Failed to update document'),
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => legalDocumentService.publish(id),
    onSuccess: () => {
      message.success('Document published');
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
    },
    onError: () => message.error('Failed to publish document'),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => legalDocumentService.archive(id),
    onSuccess: () => {
      message.success('Document archived');
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
    },
    onError: () => message.error('Failed to archive document'),
  });

  const handleOpenCreate = () => {
    setEditingDoc(null);
    setContentType('markdown');
    form.resetFields();
    setDrawerOpen(true);
  };

  const handleOpenEdit = async (id: string) => {
    try {
      const res = await legalDocumentService.getById(id);
      if (res.data) {
        setEditingDoc(res.data);
        setContentType(res.data.externalUrl ? 'url' : 'markdown');
        form.setFieldsValue(res.data);
        setDrawerOpen(true);
      }
    } catch {
      message.error('Failed to load document');
    }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const contentFields =
      contentType === 'url'
        ? { externalUrl: values.externalUrl, contentMarkdown: undefined }
        : { contentMarkdown: values.contentMarkdown, externalUrl: undefined };

    if (editingDoc) {
      updateMutation.mutate({
        id: editingDoc.id,
        data: {
          title: values.title,
          ...contentFields,
          requiresAcceptance: values.requiresAcceptance,
          gracePeriodDays: values.gracePeriodDays,
        },
      });
    } else {
      createMutation.mutate({ ...values, ...contentFields });
    }
  };

  const handleOpenAcceptances = (docId: string) => {
    setAcceptanceDocId(docId);
    setAcceptanceFilter('accepted');
    setAcceptancePage(1);
    initializedRef.current = true;
    setAcceptanceModalOpen(true);
  };

  const handleExportCSV = async () => {
    if (!acceptanceDocId) return;
    try {
      const url = legalDocumentService.getAcceptanceExportUrl(acceptanceDocId);
      window.open(url, '_blank');
    } catch {
      message.error('Failed to export CSV');
    }
  };

  const acceptanceColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_: unknown, record: DocumentAcceptanceUserEntry) =>
        [record.firstName, record.lastName].filter(Boolean).join(' ') || '-',
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => <Tag color={status === 'accepted' ? 'green' : 'orange'}>{status}</Tag>,
    },
    {
      title: 'Accepted At',
      dataIndex: 'acceptedAt',
      key: 'acceptedAt',
      width: 180,
      render: (date: string) => (date ? new Date(date).toLocaleString('de-DE') : '-'),
    },
    {
      title: 'Version',
      dataIndex: 'acceptedVersion',
      key: 'acceptedVersion',
      width: 80,
      render: (v: string) => v || '-',
    },
  ];

  const columns = [
    {
      title: 'Dokument',
      key: 'document',
      width: 320,
      render: (_: unknown, record: LegalDocumentSummary) => (
        <div className="table-cell-stack">
          <Text strong>{record.title}</Text>
          <Text className="table-cell-muted">
            {record.slug} · Version {record.version}
          </Text>
          {record.externalUrl && (
            <Typography.Link href={record.externalUrl} target="_blank" rel="noopener noreferrer">
              Externes Dokument <LinkOutlined />
            </Typography.Link>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: LegalDocumentStatus) => <Tag color={STATUS_COLORS[status]}>{status}</Tag>,
    },
    {
      title: 'Acceptance',
      key: 'acceptance',
      width: 130,
      render: (_: unknown, record: LegalDocumentSummary) =>
        record.requiresAcceptance ? (
          <Tag icon={<CheckCircleOutlined />} color="blue">
            Required ({record.gracePeriodDays}d)
          </Tag>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Published',
      dataIndex: 'publishedAt',
      key: 'publishedAt',
      width: 160,
      render: (date: string) => (date ? new Date(date).toLocaleString('de-DE') : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 190,
      render: (_: unknown, record: LegalDocumentSummary) => (
        <Space wrap>
          {record.status === LegalDocumentStatus.DRAFT && (
            <>
              <Button size="small" onClick={() => handleOpenEdit(record.id)}>
                Edit
              </Button>
              <Popconfirm
                title="Publish this document?"
                description={
                  <>
                    The current active version (if any) will be archived.
                    {record.requiresAcceptance && (
                      <>
                        <br />
                        Users will be prompted to accept. After {record.gracePeriodDays} day(s), non-accepting users
                        will be blocked.
                      </>
                    )}
                  </>
                }
                onConfirm={() => publishMutation.mutate(record.id)}
              >
                <Button size="small" type="primary" icon={<SendOutlined />}>
                  Publish
                </Button>
              </Popconfirm>
            </>
          )}
          {record.status === LegalDocumentStatus.ACTIVE && (
            <>
              <Button size="small" icon={<TeamOutlined />} onClick={() => handleOpenAcceptances(record.id)}>
                Users
              </Button>
              <Popconfirm title="Archive this document?" onConfirm={() => archiveMutation.mutate(record.id)}>
                <Button size="small" danger icon={<StopOutlined />}>
                  Archive
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Dokumente, Versionen und Zustimmungen verwalten.</Typography.Text>
      </div>
      <>
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            placeholder="Filter by slug"
            style={{ width: 200 }}
            allowClear
            onChange={value => setSlugFilter(value)}
          >
            {(slugsData || []).map(s => (
              <Select.Option key={s.slug} value={s.slug}>
                {s.slug} {s.activeVersion ? `(v${s.activeVersion})` : ''}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder="Filter by status"
            style={{ width: 150 }}
            allowClear
            onChange={value => setStatusFilter(value)}
          >
            {Object.values(LegalDocumentStatus).map(s => (
              <Select.Option key={s} value={s}>
                {s}
              </Select.Option>
            ))}
          </Select>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
            New Document
          </Button>
        </Space>

        <Table
          tableLayout="fixed"
          scroll={{ x: 900 }}
          columns={columns}
          dataSource={listData?.documents || []}
          rowKey="id"
          loading={isLoading}
          pagination={{ defaultPageSize: 20 }}
        />

        <Drawer
          title={editingDoc ? `Edit: ${editingDoc.slug} v${editingDoc.version}` : 'New Legal Document'}
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setEditingDoc(null);
          }}
          width={720}
          extra={
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editingDoc ? 'Save' : 'Create'}
            </Button>
          }
        >
          <Form form={form} layout="vertical">
            {!editingDoc && (
              <>
                <Form.Item
                  name="slug"
                  label={
                    <Space wrap>
                      Slug
                      <Tooltip title="Identifies the document type. Use a predefined slug to link with the mobile app, or create a custom one.">
                        <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                      </Tooltip>
                    </Space>
                  }
                  rules={[{ required: true }]}
                >
                  <AutoComplete
                    placeholder="Select or type a slug"
                    options={PREDEFINED_SLUGS.map(s => ({
                      value: s.value,
                      label: (
                        <div>
                          <Text strong>{s.value}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {s.description}
                          </Text>
                        </div>
                      ),
                    }))}
                    filterOption={(input, option) =>
                      !!option && String(option.value).toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
                <Form.Item name="version" label="Version" rules={[{ required: true }]}>
                  <Input placeholder="e.g. 1.0" />
                </Form.Item>
              </>
            )}
            <Form.Item name="title" label="Title" rules={[{ required: true }]}>
              <Input placeholder="e.g. Allgemeine Geschäftsbedingungen" />
            </Form.Item>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="How acceptance and blocking work"
              description={
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                  <li>
                    <strong>Requires Acceptance OFF</strong> — informational only, users are never prompted or blocked.
                  </li>
                  <li>
                    <strong>Requires Acceptance ON</strong> — users see a banner asking them to review &amp; accept.
                  </li>
                  <li>
                    <strong>Grace Period</strong> — days after publishing before non-accepting users get{' '}
                    <strong>fully blocked</strong> from the app. Set to 0 for immediate blocking.
                  </li>
                  <li>
                    <strong>Publishing a new version</strong> — archives the previous active version. Users who accepted
                    the old version will be prompted to re-accept.
                  </li>
                </ul>
              }
            />
            <Space wrap style={{ marginBottom: 16 }}>
              <Form.Item
                name="requiresAcceptance"
                label="Requires Acceptance"
                valuePropName="checked"
                initialValue={false}
                style={{ marginBottom: 0 }}
              >
                <Switch />
              </Form.Item>
              <Form.Item
                name="gracePeriodDays"
                label={
                  <Space wrap>
                    Grace Period (days)
                    <Tooltip title="After this many days post-publish, users who haven't accepted will be locked out of the app until they accept. Only applies when Requires Acceptance is ON.">
                      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                  </Space>
                }
                initialValue={14}
                style={{ marginBottom: 0 }}
              >
                <InputNumber min={0} max={365} />
              </Form.Item>
            </Space>
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Content Source
              </Text>
              <Segmented
                value={contentType}
                onChange={val => setContentType(val as 'markdown' | 'url')}
                options={[
                  { label: 'Markdown', value: 'markdown' },
                  { label: 'External URL', value: 'url' },
                ]}
              />
            </div>
            {contentType === 'markdown' ? (
              <Form.Item
                name="contentMarkdown"
                label="Content (Markdown)"
                rules={[{ required: !editingDoc && contentType === 'markdown' }]}
                trigger="onChange"
                getValueFromEvent={e => e}
              >
                <MarkdownEditorWithPreview />
              </Form.Item>
            ) : (
              <Form.Item
                name="externalUrl"
                label="External URL"
                rules={[
                  { required: contentType === 'url', message: 'URL is required' },
                  { type: 'url', message: 'Must be a valid URL' },
                ]}
              >
                <Input placeholder="https://mygateapp.de/legal/terms" />
              </Form.Item>
            )}
          </Form>
        </Drawer>

        <Modal
          title="Document Acceptances"
          open={acceptanceModalOpen}
          onCancel={() => {
            setAcceptanceModalOpen(false);
            setAcceptanceDocId(null);
            initializedRef.current = false;
          }}
          footer={null}
          width={800}
        >
          <Space wrap style={{ marginBottom: 16 }}>
            <Text>
              Accepted: <Text strong>{acceptanceData?.acceptedCount ?? 0}</Text> | Pending:{' '}
              <Text strong>{acceptanceData?.pendingCount ?? 0}</Text>
            </Text>
            <Button icon={<DownloadOutlined />} onClick={handleExportCSV}>
              Export CSV
            </Button>
          </Space>
          <Tabs
            activeKey={acceptanceFilter}
            onChange={key => setAcceptanceFilter(key as 'accepted' | 'pending')}
            items={[
              { key: 'accepted', label: `Accepted (${acceptanceData?.acceptedCount ?? 0})` },
              { key: 'pending', label: `Not Accepted (${acceptanceData?.pendingCount ?? 0})` },
            ]}
          />
          <Table
            scroll={{ x: 'max-content' }}
            columns={acceptanceColumns}
            dataSource={acceptanceData?.users || []}
            rowKey="userId"
            loading={acceptanceLoading}
            pagination={{
              current: acceptancePage,
              pageSize: 50,
              total: acceptanceData?.total || 0,
              onChange: page => setAcceptancePage(page),
            }}
            size="small"
          />
        </Modal>
      </>
    </>
  );
}
